// The app's whole haptic vocabulary. Five patterns, deliberately — a buzz on
// every tap is how staff learn to switch the feature off, so this stays at the
// moments where the screen alone leaves a real doubt.
//
// Durations in milliseconds; an array alternates buzz and pause. They are short
// on purpose: anything a user can consciously time has already outstayed its
// welcome.
export const HAPTIC_PATTERNS = {
  /** A press-and-hold crossed its threshold. The finger is still and nothing
      has moved, so this is the only thing that says the hold took. */
  tick: 8,
  /** A page turned under a swipe. */
  page: 12,
  /** The swipe went nowhere — the end of the range. Two taps, because a single
      one is what a successful page feels like, and "nothing happened" must not
      be mistaken for "something did". */
  bump: [10, 40, 10],
  /** A write landed. */
  ok: [12, 55, 20],
  /** A write did not. Longer and doubled, so failure can never be mistaken for
      success by feel alone — the one distinction in here that genuinely
      matters. */
  fail: [28, 45, 28],
} as const;

export type Haptic = keyof typeof HAPTIC_PATTERNS;

// WHERE THIS ACTUALLY FIRES: Chrome and Samsung Internet on Android, and
// nowhere else that matters to this app. Safari on iOS has never implemented
// the Vibration API at any version, and Chrome on iOS is Safari underneath, so
// an iPhone feels none of this. Desktop browsers expose the method and have no
// motor behind it, so it is a silent no-op there too.
//
// That is the reason for the shape below rather than a bare call. The layer is
// allowed to do nothing; it is not allowed to be the reason something else
// fails. The API also demands sticky user activation and can be refused
// outright by a device that is on silent or in Do Not Disturb — all of which
// arrives as either `false` or a throw, and neither is this caller's business.
export function haptic(kind: Haptic): void {
  try {
    navigator.vibrate?.(HAPTIC_PATTERNS[kind] as number | number[]);
  } catch {
    // A refusal is not an error worth propagating to a booking form.
  }
}
